import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Canvas } from 'fabric';

import { Shape, Tool, SUB_MENU_PORTAL_ID } from '../draw-canvas.constants';
import { useContextualSurface } from 'approot/shared/ally/contextual.hooks';
import IconShapes from 'approot/shared/inline-svg/activity-builder/icon-shapes';
import IconCircle from 'approot/shared/inline-svg/activity-builder/icon-circle';
import IconSquare from 'approot/shared/inline-svg/activity-builder/icon-square';
import IconTriangle from 'approot/shared/inline-svg/activity-builder/icon-triangle';
import IconLine from 'approot/shared/inline-svg/activity-builder/icon-line';
import { useDrawCanvasContext } from '../draw-canvas.context';
import { ToolbarItems, ToolBarTool } from './toolbar.style';
import ToolbarItem from './toolbar-item';

type Props = {
  canvas?: Canvas | null;
};

const ToolbarItemShapes = ({ canvas }: Props) => {
  const { toolSet, setToolSet, turnOffDrawMode } = useDrawCanvasContext();

  const [isActive, setIsActive] = useState<boolean>(false);

  const { styles, attributes, setReferenceElement, setPopperElement } =
    useContextualSurface(isActive, setIsActive, {
      offset: [0, 8],
      placement: 'left-start',
      strategy: 'fixed',
    });

  const portalContainer = document.getElementById(SUB_MENU_PORTAL_ID);

  const renderShapesPanel = () => (
    <div
      ref={setPopperElement}
      role="dialog"
      tabIndex={-1}
      style={styles.popper}
      {...attributes.popper}
    >
      <ToolbarItems>
        <ToolbarItem hoverText="Line">
          <ToolBarTool
            data-selector="toolbar-shape-line"
            $isActive={toolSet?.shape === Shape.Line}
            onClick={() => {
              setToolSet({ tool: Tool.Shapes, shape: Shape.Line });
              setIsActive(false);
            }}
          >
            <IconLine width={24} height={24} />
          </ToolBarTool>
        </ToolbarItem>
        <ToolbarItem hoverText="Circle">
          <ToolBarTool
            data-selector="toolbar-shape-circle"
            $isActive={toolSet?.shape === Shape.Circle}
            onClick={() => {
              setToolSet({ tool: Tool.Shapes, shape: Shape.Circle });
              setIsActive(false);
            }}
          >
            <IconCircle width={24} height={24} />
          </ToolBarTool>
        </ToolbarItem>
        <ToolbarItem hoverText="Triangle">
          <ToolBarTool
            data-selector="toolbar-shape-triangle"
            $isActive={toolSet?.shape === Shape.Triangle}
            onClick={() => {
              setToolSet({ tool: Tool.Shapes, shape: Shape.Triangle });
              setIsActive(false);
            }}
          >
            <IconTriangle width={24} height={24} />
          </ToolBarTool>
        </ToolbarItem>
        <ToolbarItem hoverText="Square">
          <ToolBarTool
            data-selector="toolbar-shape-square"
            $isActive={toolSet?.shape === Shape.Rectangle}
            onClick={() => {
              setToolSet({ tool: Tool.Shapes, shape: Shape.Rectangle });
              setIsActive(false);
            }}
          >
            <IconSquare width={24} height={24} />
          </ToolBarTool>
        </ToolbarItem>
      </ToolbarItems>
    </div>
  );

  return (
    <>
      <ToolbarItem hoverText="Shapes">
        <ToolBarTool
          data-selector="toolbar-shapes"
          ref={setReferenceElement}
          $isActive={toolSet?.tool === Tool.Shapes}
          onClick={() => {
            if (canvas) {
              canvas.discardActiveObject();
              canvas.renderAll();
              turnOffDrawMode(canvas);
              setIsActive(!isActive);
              setToolSet({ tool: Tool.Shapes });
            }
          }}
        >
          <IconShapes width={24} height={24} />
        </ToolBarTool>
      </ToolbarItem>
      {isActive &&
        toolSet &&
        (portalContainer
          ? createPortal(renderShapesPanel(), portalContainer)
          : renderShapesPanel())}
    </>
  );
};
export default ToolbarItemShapes;
